import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  schemas,
  type CreateAreaRequest,
  type RoomAreaListItem,
  type UpdateAreaRequest,
} from '@kreart/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, TextField, FormErrorBanner } from '@/components/form';
import { useCreateArea, useUpdateArea } from '../hooks';
import { errorKeyFor } from '../errorMessages';

interface Props {
  projectId: string;
  roomId: string;
  area: RoomAreaListItem | null;
  open: boolean;
  onClose: () => void;
  /** Called with the freshly created area (create mode only) — used by the picker. */
  onCreated?: (area: RoomAreaListItem) => void;
}

type FormValues = {
  name: string;
};

const formSchema = schemas.createAreaRequest;

export function AreaDialog({ projectId, roomId, area, open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const isEdit = area !== null;

  const createMut = useCreateArea(projectId, roomId);
  const updateMut = useUpdateArea(projectId, roomId, area?.id ?? '');
  const mut = isEdit ? updateMut : createMut;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      name: area?.name ?? '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      if (isEdit) {
        await updateMut.mutateAsync({ name: values.name } as UpdateAreaRequest);
      } else {
        const created = await createMut.mutateAsync({ name: values.name } as CreateAreaRequest);
        onCreated?.(created);
      }
      form.reset();
      onClose();
    } catch {
      /* surfaced via mut.error */
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !mut.isPending) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('projects.areas.editTitle') : t('projects.areas.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('projects.areas.description')}</DialogDescription>
        </DialogHeader>
        <Form form={form} onSubmit={onSubmit} className="space-y-3">
          <TextField name="name" label={t('projects.areas.fields.name')} />

          <FormErrorBanner error={mut.error} resolveKey={errorKeyFor} />

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={mut.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? t('common.loading') : t('common.submit')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
